import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@livecommerce/design-system';
import {
  CHAT_PRODUCT,
  ONE_TO_MANY_PRODUCT,
  ONE_TO_ONE_PRODUCT,
  ON_DEMAND_PRODUCT,
} from 'constants/products.js';
import {
  ORG_ONE_TO_MANY_HOME,
  ORG_ONE_TO_ONE_HOME,
  buildRoute,
  ORG_ON_DEMAND_HOME,
  ORG_ON_DEMAND_VIDEOS,
  PRODUCT_ROUTES,
} from 'constants/routes.js';
import usePermissions from 'hooks/usePermissions.jsx';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import SkeletonCell from 'components-v3/List/cells/SkeletonCell.jsx';
import {
  CustomProductSwitcher,
  SkeletonListWrapper,
  SkeletonWrapper,
  SkeletonColumn,
  StyledSkeletonCell,
  SwitchersWrapper,
  SwitchersList,
  SwitchersListItem,
  GroupsWrapper,
  productLabelStyles,
} from './styles.js';
import Labels from 'components/Labels/index.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { clearAllProductLastPaths, readProductLastPath } from 'utils/productLastPath.js';

const SOCIAL_COMMERCE = 'social-commerce';
const DIGITAL_CLIENTELING = 'digital-clienteling';

const PRODUCT_LIST_LABELS_BY_GROUP = {
  [SOCIAL_COMMERCE]: 'Social Commerce',
  [DIGITAL_CLIENTELING]: 'Digital Clienteling',
};

const OrgProductList = ({ selectedOrg, currentOrg, selectedProduct, selectingProduct, showSkeleton }) => {
  const history = useHistory();
  const { hasOrgSpecificProductPermission } = usePermissions();
  const { features } = useFeatureContext();
  const { t } = useLocalization();

  // If it is the same org or no org selected
  const isSameOrg = selectedOrg?.id === currentOrg?.id || selectedOrg === null;

  const getProductList = useCallback(
    ({ id, features } = {}, isSameOrg) => {
      const products = [];
      if (!features) return products;

      if (features.shows?.enabled) {
        products.push({
          id: ONE_TO_MANY_PRODUCT,
          selected: isSameOrg && selectedProduct === ONE_TO_MANY_PRODUCT,
          link: buildRoute(ORG_ONE_TO_MANY_HOME, { orgId: id }),
          orgId: id,
          group: SOCIAL_COMMERCE,
          disabled: !hasOrgSpecificProductPermission(id, ONE_TO_MANY_PRODUCT),
        });
      }

      if (features.vod?.enabled) {
        products.push({
          id: ON_DEMAND_PRODUCT,
          selected: isSameOrg && selectedProduct === ON_DEMAND_PRODUCT,
          link: buildRoute(features.vodHomePage?.enabled ? ORG_ON_DEMAND_HOME : ORG_ON_DEMAND_VIDEOS, { orgId: id }),
          orgId: id,
          group: SOCIAL_COMMERCE,
          disabled: !hasOrgSpecificProductPermission(id, ON_DEMAND_PRODUCT),
        });
      }
      if (features.calls?.enabled) {
        products.push({
          id: ONE_TO_ONE_PRODUCT,
          selected: isSameOrg && selectedProduct === ONE_TO_ONE_PRODUCT,
          link: buildRoute(ORG_ONE_TO_ONE_HOME, { orgId: id }),
          orgId: id,
          group: DIGITAL_CLIENTELING,
          disabled: !hasOrgSpecificProductPermission(id, ONE_TO_ONE_PRODUCT),
        });
      }
      if (features.chat?.enabled) {
        products.push({
          id: CHAT_PRODUCT,
          selected: isSameOrg && selectedProduct === CHAT_PRODUCT,
          link: buildRoute(PRODUCT_ROUTES.ORG_CHAT_HOME, { orgId: id, selectedProduct: CHAT_PRODUCT }),
          orgId: id,
          group: DIGITAL_CLIENTELING,
          disabled: !hasOrgSpecificProductPermission(id, CHAT_PRODUCT),
        });
      }
      return products;
    },
    [hasOrgSpecificProductPermission, selectedProduct],
  );

  const productList = useMemo(() => {
    if (isSameOrg) return getProductList(currentOrg, isSameOrg);
    if (selectedOrg) return getProductList(selectedOrg);
    return [];
  }, [isSameOrg, getProductList, currentOrg, selectedOrg]);

  const handleProductSelect = useCallback(
    (product) => {
      if (!product || product.disabled) return;
      const isSameProduct = isSameOrg && selectedProduct === product?.id;
      trackingService.track({
        eventType: `select-product:${isSameProduct ? 'same' : product?.id}`,
        previousSelectedProduct: selectedProduct,
      });
      if (isSameProduct) return;
      selectingProduct();
      if (!product?.link) return;

      // Try to read the last visited path for the product, if exists,
      // redirect to it. Otherwise, redirect to the default product link
      const rememberedPath = readProductLastPath(product.orgId, product.id);
      history.push(rememberedPath || product.link);
    },
    [isSameOrg, selectedProduct, selectingProduct, history],
  );

  useEffect(() => {
    if (isSameOrg) return;
    // Wipe remembered per-product paths so the new org starts from scratch
    clearAllProductLastPaths();
    // Select the first product in the list when the org is changed
    const productList = getProductList(selectedOrg);
    const enabledProducts = productList.filter((product) => !product.disabled);
    const _selectedProduct = enabledProducts.find((product) => product.id === selectedProduct);
    if (_selectedProduct) {
      // If the new workspace has the same product type as the selected product, switch to the same product
      handleProductSelect(_selectedProduct);
    } else {
      // If the current selected product does not exist in the productList, select the first accessible product
      handleProductSelect(enabledProducts[0]);
    }
  }, [selectedOrg, isSameOrg, selectingProduct, selectedProduct, getProductList, handleProductSelect]);

  const groupedProductList = productList.reduce((groups, product) => {
    if (!groups[product.group]) groups[product.group] = [];

    groups[product.group].push(product);
    return groups;
  }, {});

  if (showSkeleton) {
    return (
      <SkeletonListWrapper layout>
        {productList.map((product) => (
          <SkeletonWrapper key={`skeleton-wrapper-for-${product?.id}`}>
            <SkeletonCell width={44} height={44} borderRadius={6} />
            <SkeletonColumn>
              <StyledSkeletonCell width={146} height={16} borderRadius={16} />
              <StyledSkeletonCell width={96} height={12} borderRadius={12} />
            </SkeletonColumn>
          </SkeletonWrapper>
        ))}
      </SkeletonListWrapper>
    );
  }

  return (
    <GroupsWrapper layout>
      {Object.entries(groupedProductList).map(([key, products]) => (
        <SwitchersWrapper key={key} id={`product-switcher:group:${key}`}>
          <Labels
            label={PRODUCT_LIST_LABELS_BY_GROUP[key]}
            labelVariation="roman"
            externalLabelsClassName={productLabelStyles}
          />
          <SwitchersList>
            {products.map((product) => {
              const switcher = (
                <CustomProductSwitcher
                  isSelected={product?.selected ?? false}
                  product={product?.id}
                  onSelect={() => handleProductSelect(product)}
                  disabled={product?.disabled}
                />
              );
              return (
                <SwitchersListItem key={product?.id} id={`product-switcher:product:${product?.id}`}>
                  {product?.disabled ? (
                    <Tooltip
                      id={`product-switcher:no-access-tooltip:${product?.id}`}
                      tooltipText={t(
                        'genericLabels.productSwitcherNoAccessTooltip',
                        {},
                        'You don’t have access to this product. Contact your admin if you believe this is a mistake.',
                      )}
                      tooltipPosition="bottom"
                      minWidth="300px"
                    >
                      {switcher}
                    </Tooltip>
                  ) : (
                    switcher
                  )}
                </SwitchersListItem>
              );
            })}
          </SwitchersList>
        </SwitchersWrapper>
      ))}
    </GroupsWrapper>
  );
};

OrgProductList.propTypes = {
  selectedOrg: PropTypes.object,
  currentOrg: PropTypes.object,
  selectedProduct: PropTypes.string,
  selectingProduct: PropTypes.func.isRequired,
};

export default OrgProductList;
